import type { ApiError } from '@/api/errorHandler';

export type AuthFailureReason = 'sessionExpired' | 'unauthorized';

export interface AuthFailureEventDetail {
  reason: AuthFailureReason;
  status: 401 | 403;
  code?: string;
}

export const authFailureEventName = 'entity-portal:auth-failure';

const unauthorizedCodes = new Set<string>([
  'auth.permission_denied',
  'auth.insufficient_permissions',
]);

const sessionExpiredCodes = new Set<string>([
  'auth.missing_token',
  'auth.invalid_or_missing_token',
  'auth.invalid_or_expired_token',
  'auth.missing_subject',
]);

const normalizeCode = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : undefined;
};

const resolveAuthFailure = (error: ApiError): AuthFailureEventDetail | null => {
  const normalizedCode = normalizeCode(error.code);

  if (error.status === 401) {
    if (!normalizedCode || sessionExpiredCodes.has(normalizedCode)) {
      return {
        reason: 'sessionExpired',
        status: 401,
        code: normalizedCode,
      };
    }

    return null;
  }

  if (error.status === 403) {
    if (!normalizedCode || unauthorizedCodes.has(normalizedCode)) {
      return {
        reason: 'unauthorized',
        status: 403,
        code: normalizedCode,
      };
    }
  }

  return null;
};

export const emitAuthFailureFromApiError = (error: ApiError): void => {
  const failure = resolveAuthFailure(error);

  if (!failure) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AuthFailureEventDetail>(authFailureEventName, {
      detail: failure,
    }),
  );
};
